import React, { useEffect, useMemo } from 'react';
import { TTag } from './types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';
import { isFunction, merge, reduce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { selectApiState } from '../../../store/selector/selectApiState';
import { fnv1aHash } from '../../../utils/fnv1aHash';
import { IApiRequestConfig } from '../../../store/saga/api/types';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { ApiRequest } from '../../../store/saga/api/actions';
import { TValidationScheme } from '@krugom/validator/dist/types';
import { setApiState } from '../../../store/reducer';
import { requestParamsSchema } from './schema';

const validationScheme: TValidationScheme = {
  type: 'object',
  properties: {
    request: requestParamsSchema,
  },
};

const createKey = (reqProps: Partial<IApiRequestConfig>) => {
  try {
    return fnv1aHash(JSON.stringify(reqProps));
  } catch {
    return 'unknown';
  }
};

export const CustomRequest: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const result = validate(tagProps, validationScheme);
  const key = result.isValid ? tagProps.request?.key || createKey(tagProps.request) : 'unknown';
  const dispatch = useDispatch();
  const requestState = useSelector((state: RootState) => selectApiState(state, key)) || {
    status: 'init',
  };

  useEffect(() => {
    if (result.isValid && requestState.status !== 'progress' && tagProps.request?.submitOnLoad) {
      dispatch(ApiRequest(tagProps.request));
    }
  }, [result.isValid, requestState.status, tagProps.request?.submitOnLoad]);

  const handleSubmit = (handlerParams?: Partial<TTag['request']>) => {
    const mergedParams = merge({}, tagProps.request, handlerParams || {}, { key });
    const requestParams = reduce(
      mergedParams,
      (r, v, k) => ({ ...r, ...(/^on[A-Z]/.test(k) ? {} : { [k]: v }) }),
      {} as TTag['request']
    );
    const res = validate(requestParams, requestParamsSchema);
    if (res.isValid) {
      dispatch(ApiRequest(requestParams));
    }
  };

  useEffect(() => {
    if (requestState.status === 'succeed' && requestState.execActions) {
      dispatch(setApiState({ key, state: { ...requestState, execActions: false } }));
      if (isFunction(tagProps.request.onSucceed)) tagProps.request.onSucceed();
    }
    if (requestState.status === 'rejected' && requestState.execActions) {
      dispatch(setApiState({ key, state: { ...requestState, execActions: false } }));
      if (isFunction(tagProps.request.onRejected)) tagProps.request.onRejected();
    }
  }, [requestState.status, requestState.execActions]);

  const requestContext = useMemo(
    () => ({
      ...requestState,
      key,
      handleSubmit,
    }),
    [requestState, key, handleSubmit]
  );

  const childrenContext = useMemo(
    () => ({
      ...context,
      request: requestContext,
    }),
    [context, requestContext]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $RequestContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }
  return <UniversalChildren context={childrenContext}>{tagProps.children}</UniversalChildren>;
});
CustomRequest.displayName = 'CustomRequest';
